import { Injectable } from '@angular/core';
import { Server, Response } from 'miragejs';
import { environment } from '@htx/environment';
import {
  getOrganizations,
  addOrganization,
  updateOrganizationStateChange,
  getMembers,
  updateOrganizationData,
  getMemberDetails,
  getMemberByIamId,
  updateMemberData,
  getOrganizationDocument,
  addMember
} from './data/organizations/mocked-organizations';
import { getComments, addComment } from './data/comments/mocked-comments';
import {
  getUseCaseRequests,
  getUseCaseRequestById,
  getUseCaseComments,
  addUseCaseComment,
  updateUseCaseRequestState,
  createUseCaseRequest
} from './data/use-case-participation/mocked-use-case-participation';
import {
  getMockedAuthToken,
  getMockedUserId,
  getMockedUserRoles,
  isLoggedIn,
  getAuthMockMode$
} from './data/auth/mocked-auth';
import {
  CommentType,
  SpringPageApiDto,
  OrganizationDetailsApiDto,
  AdmissionState,
  ActivityState,
  PartnerListItemApiDto,
  PartnerDetailApiDto,
  OrganizationDto,
  OrganizationUpdateDto
} from '../services/organizations/organization.model';

@Injectable({
  providedIn: 'root'
})
export class MockService {
  private mirageServer?: Server;

  public mirageJsServer(): Server {
    console.log('Starting MirageJS server for mocks...');
    console.log('Backend URL:', environment.backendUrl);
    this.mirageServer = new Server({
      routes(): void {
        this.get(environment.backendUrl + '/organizations/partners', (schema, request) => {
          return MockService.partnersWithPagination(request);
        });

        this.get(environment.backendUrl + '/organizations/partners/:id', (schema, request) => {
          const id = request.params['id'];
          return MockService.partnerDetails(id);
        });

        this.get(environment.backendUrl + '/organizations', (schema, request) => {
          return MockService.organizationsWithPagination(request);
        });

        this.get(environment.backendUrl + '/organizations/me', () => {
          return MockService.myOrganizationDetails();
        });

        this.get(environment.backendUrl + '/organizations/:id/members', (schema, request) => {
          const orgId = request.params['id'];
          return MockService.membersByOrgIdPaginated(orgId, request);
        });

        this.get(environment.backendUrl + '/members/me', () => {
          return MockService.currentUserMember();
        });

        this.put(environment.backendUrl + '/members/me', (schema, request) => {
          const iamId = getMockedUserId();
          const member = getMemberByIamId(iamId);
          if (!member) {
            return new Response(404, {}, JSON.stringify({ error: 'Member not found' }));
          }
          const body = JSON.parse(request.requestBody);
          const updated = updateMemberData(member.id, body);
          return updated
            ? new Response(204, {}, '')
            : new Response(404, {}, JSON.stringify({ error: 'Member not found' }));
        });

        this.get(environment.backendUrl + '/members/:memberId', (schema, request) => {
          const memberId = request.params['memberId'];
          return MockService.memberDetailsById(memberId);
        });

        this.put(environment.backendUrl + '/members/:memberId', (schema, request) => {
          const memberId = request.params['memberId'];
          const body = JSON.parse(request.requestBody);

          const updatedMember = updateMemberData(memberId, body);
          if (updatedMember) {
            return new Response(204, {}, '');
          }
          return new Response(404, {}, JSON.stringify({ error: 'Member not found' }));
        });

        this.post(environment.backendUrl + '/members', (schema, request) => {
          const body = JSON.parse(request.requestBody);
          const newMember = addMember(body);
          return new Response(201, {}, JSON.stringify(newMember));
        });

        this.get(environment.backendUrl + '/organizations/:id', (schema, request) => {
          const id = request.params['id'];
          return MockService.organizationDetails(id);
        });

        this.get(
          environment.backendUrl + '/organizations/:id/admission/comments',
          (schema, request) => {
            const id = request.params['id'];
            return MockService.commentsByOrganization(id);
          }
        );

        this.get(environment.backendUrl + '/organizations/:id/document', (schema, request) => {
          const id = request.params['id'];
          const doc = getOrganizationDocument(id);
          if (doc) {
            return new Response(200, { 'Content-Type': 'application/pdf' }, doc as any);
          }
          return new Response(404, {}, JSON.stringify({ error: 'Document not found' }));
        });

        this.post(
          environment.backendUrl + '/organizations/:id/admission/comments',
          (schema, request) => {
            const organizationId = request.params['id'];
            const newComment = JSON.parse(request.requestBody);
            const comment = {
              id: `c${Date.now()}`,
              organizationId: organizationId,
              text: newComment.text || '',
              date: new Date().toISOString(),
              type: CommentType.COMMENT,
              author: 'Current User'
            };
            addComment(comment);
            return new Response(201, {}, JSON.stringify(comment));
          }
        );

        this.post(environment.backendUrl + '/organizations', async (schema, request) => {
          try {
            let newRegistration: any = {};
            const body = request.requestBody as any;

            if (body.entries) {
              let registrationDtoBlob: Blob | null = null;
              let file: File | null = null;

              // Single pass to collect both organizationRegistrationDto and file
              for (const [key, value] of body.entries()) {
                if (key === 'organizationRegistrationDto' && value instanceof Blob) {
                  registrationDtoBlob = value;
                } else if (key === 'file' && value instanceof File) {
                  file = value;
                }
              }

              // Parse registrationDto
              if (registrationDtoBlob) {
                const blobText = await registrationDtoBlob.text();
                newRegistration = JSON.parse(blobText);
              }

              // Add file metadata
              if (file) {
                newRegistration.document = file;
              }

              // auto orgId gen for mock
              newRegistration.id = crypto.randomUUID();

              // auto admission state
              newRegistration.admissionState = 'PENDING';

              // auto activity state
              newRegistration.activityState = 'INACTIVE';

              // Build representative info from flat fields
              newRegistration.representative = {
                title: newRegistration.title,
                firstName: newRegistration.firstName,
                lastName: newRegistration.lastName,
                email: newRegistration.email,
                phoneNumber: newRegistration.phoneNumber,
                position: newRegistration.position,
                roles: newRegistration.roles || ['ORGANIZATION_REPRESENTATIVE']
              };

              // group representative info before adding
              delete newRegistration.firstName;
              delete newRegistration.lastName;
              delete newRegistration.email;
              delete newRegistration.phoneNumber;
              delete newRegistration.title;
              delete newRegistration.position;
              delete newRegistration.roles;
            }
            addOrganization(newRegistration);
            return new Response(
              201,
              {},
              JSON.stringify({
                id: newRegistration.id,
                legalName: newRegistration.legalName,
                shortName: newRegistration.shortName,
                admissionState: newRegistration.admissionState,
                activityState: newRegistration.activityState
              })
            );
          } catch (error) {
            console.error('Error processing registration:', error);
            console.error('Request body was:', request.requestBody);
            return new Response(
              500,
              {},
              JSON.stringify({
                error: 'Failed to process registration',
                details: (error as Error).message
              })
            );
          }
        });

        this.put(
          environment.backendUrl + '/organizations/:id/admission/approve',
          (schema, request) => {
            const id = request.params['id'];
            const body = JSON.parse(request.requestBody);
            const reasoning = body.reasoning || '';
            const organization = MockService.updateOrganizationState(
              id,
              AdmissionState.APPROVED,
              reasoning
            );
            if (organization) {
              return new Response(204, {}, '');
            }
            return new Response(404, {}, JSON.stringify({ error: 'Organization not found' }));
          }
        );

        this.put(
          environment.backendUrl + '/organizations/:id/admission/reject',
          (schema, request) => {
            const id = request.params['id'];
            const body = JSON.parse(request.requestBody);
            const reasoning = body.reasoning || '';
            const organization = MockService.updateOrganizationState(
              id,
              AdmissionState.REJECTED,
              reasoning
            );
            if (organization) {
              return new Response(204, {}, '');
            }
            return new Response(404, {}, JSON.stringify({ error: 'Organization not found' }));
          }
        );

        this.put(environment.backendUrl + '/organizations/:id', (schema, request) => {
          const id = request.params['id'];
          const body = JSON.parse(request.requestBody);

          // Create update object with the fields provided
          const updates: any = {
            legalName: body.legalName,
            shortName: body.shortName,
            legalForm: body.legalForm,
            sector: body.sector,
            website: body.website,
            description: body.description,
            commercialRegisterNumber: body.commercialRegisterNumber,
            commercialRegisterCourt: body.commercialRegisterCourt,
            taxIdNumber: body.taxIdNumber,
            did: body.did,
            activityState: body.activityState,
            foundingDate: body.foundingDate,
            street: body.street,
            houseNumber: body.houseNumber,
            postalCode: body.postalCode,
            city: body.city,
            country: body.country
          };

          // Remove undefined values
          Object.keys(updates).forEach((key) => {
            if (updates[key] === undefined) {
              delete updates[key];
            }
          });

          // Persist the updates
          updateOrganizationData(id, updates);

          return new Response(204, {}, '');
        });

        // ── Use-Case Participation ──────────────────────────────────

        this.get(environment.backendUrl + '/use-cases/participation', () => {
          return getUseCaseRequests();
        });

        this.get(environment.backendUrl + '/use-cases/participation/:id', (schema, request) => {
          const id = request.params['id'];
          const entry = getUseCaseRequestById(id);
          if (entry) {
            return entry;
          }
          return new Response(404, {}, JSON.stringify({ error: 'Participation not found' }));
        });

        this.get(
          environment.backendUrl + '/use-cases/:useCaseId/participation/status',
          (schema, request) => {
            const useCaseId = request.params['useCaseId'];
            const myOrg = MockService.myOrganizationRaw();
            const allRequests = getUseCaseRequests();
            const match = allRequests.find(
              (r) => r.useCaseId === useCaseId && r.organizationId === myOrg.id
            );
            if (match) {
              return match;
            }
            return new Response(204, {}, '');
          }
        );

        this.post(
          environment.backendUrl + '/use-cases/:useCaseId/participation',
          (schema, request) => {
            const useCaseId = request.params['useCaseId'];
            const myOrg = MockService.myOrganizationRaw();
            const entry = createUseCaseRequest(
              useCaseId,
              myOrg.id,
              myOrg.legalName,
              myOrg.representativeName
            );
            return new Response(201, {}, JSON.stringify(entry));
          }
        );

        this.put(
          environment.backendUrl + '/use-cases/participation/:id/approve',
          (schema, request) => {
            const id = request.params['id'];
            const body = JSON.parse(request.requestBody);
            const updated = updateUseCaseRequestState(
              id,
              AdmissionState.APPROVED,
              body.reasoning || ''
            );
            if (updated) {
              return new Response(204, {}, '');
            }
            return new Response(404, {}, JSON.stringify({ error: 'Participation not found' }));
          }
        );

        this.put(
          environment.backendUrl + '/use-cases/participation/:id/reject',
          (schema, request) => {
            const id = request.params['id'];
            const body = JSON.parse(request.requestBody);
            const updated = updateUseCaseRequestState(
              id,
              AdmissionState.REJECTED,
              body.reasoning || ''
            );
            if (updated) {
              return new Response(204, {}, '');
            }
            return new Response(404, {}, JSON.stringify({ error: 'Participation not found' }));
          }
        );

        this.get(
          environment.backendUrl + '/use-cases/participation/:id/comments',
          (schema, request) => {
            const id = request.params['id'];
            return getUseCaseComments(id);
          }
        );

        this.post(
          environment.backendUrl + '/use-cases/participation/:id/comments',
          (schema, request) => {
            const id = request.params['id'];
            const body = JSON.parse(request.requestBody);
            const comment = {
              id: `ucc-${Date.now()}`,
              text: body.text || '',
              date: new Date().toISOString(),
              author: 'Current User',
              type: 'COMMENT'
            };
            addUseCaseComment(id, comment as any);
            return new Response(201, {}, JSON.stringify(comment));
          }
        );

        this.get(environment.backendUrl + '/health/systems', () => {
          return [
            { name: 'BDRS', status: 'UP' },
            { name: 'DIM_WALLET_STUB', status: 'UP' },
            { name: 'CONNECTOR', status: 'DOWN' },
            { name: 'DISCOVERY_SERVICE', status: 'UP' }
          ];
        });

        // ── Representative Change ───────────────────────────────────

        this.get(environment.backendUrl + '/organizations/representative-changes', () => {
          return [];
        });

        this.get(environment.backendUrl + '/organizations/representative-changes/:id', () => {
          return new Response(404, {}, JSON.stringify({ error: 'Not found' }));
        });

        this.passthrough();
      }
    });
    return this.mirageServer;
  }

  private static organizations() {
    const content = getOrganizations();
    const returnValue: SpringPageApiDto<OrganizationDetailsApiDto> = {
      content: content.map((c) => ({
        organization: {
          id: c.id,
          bpnl: c.bpnl ?? '',
          did: c.did ?? '',
          legalName: c.legalName,
          shortName: c.shortName,
          sector: c.sector,
          legalForm: c.legalForm,
          website: c.website,
          description: c.description,
          foundingDate: c.foundingDate,
          street: c.street,
          houseNumber: c.houseNumber,
          postalCode: c.postalCode,
          city: c.city,
          country: c.country,
          commercialRegisterNumber: c.commercialRegisterNumber,
          commercialRegisterCourt: c.commercialRegisterCourt,
          taxIdNumber: c.taxIdNumber,
          admissionState: c.admissionState,
          activityState: c.activityState,
          createdDate: c.createdDate ?? ''
        },
        representative: {
          id: 'rep-' + c.id,
          organizationId: c.id,
          title: c.representative.title,
          firstName: c.representative.firstName,
          lastName: c.representative.lastName,
          position: c.representative.position,
          email: c.representative.email,
          phoneNumber: c.representative.phoneNumber,
          roles: c.representative.roles || ['ORGANIZATION_REPRESENTATIVE'],
          activityState: ActivityState.ACTIVE
        }
      })),
      pageable: {
        pageNumber: 0,
        pageSize: content.length,
        sort: {
          unsorted: false,
          sorted: true,
          empty: false
        },
        offset: 0,
        unpaged: false,
        paged: true
      },
      totalElements: content.length,
      totalPages: 1,
      last: true,
      first: true,
      numberOfElements: content.length,
      size: content.length,
      number: 0,
      sort: {
        unsorted: false,
        sorted: true,
        empty: false
      },
      empty: content.length === 0
    };
    return returnValue;
  }

  private static organizationsWithPagination(request: any) {
    const allOrganizations = getOrganizations();

    // Parse query parameters
    const page = parseInt(request.queryParams?.page || '0', 10);
    const size = parseInt(request.queryParams?.size || '5', 10);
    const sort = request.queryParams?.sort || '';

    // Parse additional filters from query params
    const admissionStateFilter = request.queryParams?.admissionState;
    const activityStateFilter = request.queryParams?.activityState;
    const legalNameFilter = request.queryParams?.legalName
      ? (request.queryParams.legalName as string).toLowerCase()
      : '';

    // Apply filters
    let filtered = [...allOrganizations];

    if (admissionStateFilter) {
      filtered = filtered.filter((p) => p.admissionState === admissionStateFilter);
    }

    if (activityStateFilter) {
      filtered = filtered.filter((p) => p.activityState === activityStateFilter);
    }

    if (legalNameFilter) {
      filtered = filtered.filter(
        (p) =>
          p.legalName.toLowerCase().includes(legalNameFilter) ||
          p.shortName.toLowerCase().includes(legalNameFilter)
      );
    }

    // Map to OrganizationDetailsApiDto format
    const content: OrganizationDetailsApiDto[] = filtered.map((c) => ({
      organization: {
        id: c.id,
        bpnl: c.bpnl ?? '',
        did: c.did ?? '',
        legalName: c.legalName,
        shortName: c.shortName,
        sector: c.sector,
        legalForm: c.legalForm,
        website: c.website,
        description: c.description,
        foundingDate: c.foundingDate,
        street: c.street,
        houseNumber: c.houseNumber,
        postalCode: c.postalCode,
        city: c.city,
        country: c.country,
        commercialRegisterNumber: c.commercialRegisterNumber,
        commercialRegisterCourt: c.commercialRegisterCourt,
        taxIdNumber: c.taxIdNumber,
        admissionState: c.admissionState,
        activityState: c.activityState,
        createdDate: c.createdDate ?? ''
      },
      representative: {
        id: 'rep-' + c.id,
        organizationId: c.id,
        title: c.representative.title,
        firstName: c.representative.firstName,
        lastName: c.representative.lastName,
        position: c.representative.position,
        email: c.representative.email,
        phoneNumber: c.representative.phoneNumber,
        roles: c.representative.roles || ['ORGANIZATION_REPRESENTATIVE'],
        activityState: ActivityState.ACTIVE
      }
    }));

    // Apply sort if specified (format: "fieldName,direction")
    if (sort) {
      const [fieldName, direction] = sort.split(',');
      if (fieldName && direction) {
        const isAsc = direction.toLowerCase() === 'asc';
        content.sort((a: any, b: any) => {
          const aVal = a.organization[fieldName] ?? a[fieldName];
          const bVal = b.organization[fieldName] ?? b[fieldName];

          if (aVal < bVal) return isAsc ? -1 : 1;
          if (aVal > bVal) return isAsc ? 1 : -1;
          return 0;
        });
      }
    }

    // Calculate pagination
    const totalElements = content.length;
    const totalPages = Math.ceil(totalElements / size) || 1;
    const startIdx = page * size;
    const endIdx = Math.min(startIdx + size, totalElements);
    const paginatedContent = content.slice(startIdx, endIdx);
    const isLast = page >= totalPages - 1;
    const isFirst = page === 0;

    const returnValue: SpringPageApiDto<OrganizationDetailsApiDto> = {
      content: paginatedContent,
      pageable: {
        pageNumber: page,
        pageSize: size,
        sort: {
          unsorted: !sort,
          sorted: !!sort,
          empty: !sort
        },
        offset: startIdx,
        unpaged: false,
        paged: true
      },
      totalElements,
      totalPages,
      last: isLast,
      first: isFirst,
      numberOfElements: paginatedContent.length,
      size,
      number: page,
      sort: {
        unsorted: !sort,
        sorted: !!sort,
        empty: !sort
      },
      empty: totalElements === 0
    };
    return returnValue;
  }

  private static partnersWithPagination(request: any) {
    const allOrganizations = getOrganizations();

    const page = parseInt(request.queryParams?.page || '0', 10);
    const size = parseInt(request.queryParams?.size || '5', 10);
    const sort = request.queryParams?.sort || '';
    const legalNameFilter = request.queryParams?.legalName
      ? (request.queryParams.legalName as string).toLowerCase()
      : '';

    // Filter to only APPROVED + ACTIVE
    let filtered = allOrganizations.filter(
      (p) => p.admissionState === 'APPROVED' && p.activityState === 'ACTIVE'
    );

    if (legalNameFilter) {
      filtered = filtered.filter(
        (p) =>
          p.legalName.toLowerCase().includes(legalNameFilter) ||
          p.shortName.toLowerCase().includes(legalNameFilter)
      );
    }

    // Map to PartnerListItemApiDto (reduced data)
    const content: PartnerListItemApiDto[] = filtered.map((c) => ({
      id: c.id,
      bpnl: c.bpnl ?? '',
      legalName: c.legalName,
      shortName: c.shortName,
      city: c.city,
      country: c.country,
      activityState: c.activityState as ActivityState
    }));

    // Apply sort
    if (sort) {
      const [fieldName, direction] = sort.split(',');
      if (fieldName && direction) {
        const isAsc = direction.toLowerCase() === 'asc';
        content.sort((a: any, b: any) => {
          const aVal = a[fieldName];
          const bVal = b[fieldName];
          if (aVal < bVal) return isAsc ? -1 : 1;
          if (aVal > bVal) return isAsc ? 1 : -1;
          return 0;
        });
      }
    }

    const totalElements = content.length;
    const totalPages = Math.ceil(totalElements / size) || 1;
    const startIdx = page * size;
    const endIdx = Math.min(startIdx + size, totalElements);
    const paginatedContent = content.slice(startIdx, endIdx);

    const returnValue: SpringPageApiDto<PartnerListItemApiDto> = {
      content: paginatedContent,
      pageable: {
        pageNumber: page,
        pageSize: size,
        sort: { unsorted: !sort, sorted: !!sort, empty: !sort },
        offset: startIdx,
        unpaged: false,
        paged: true
      },
      totalElements,
      totalPages,
      last: page >= totalPages - 1,
      first: page === 0,
      numberOfElements: paginatedContent.length,
      size,
      number: page,
      sort: { unsorted: !sort, sorted: !!sort, empty: !sort },
      empty: totalElements === 0
    };
    return returnValue;
  }

  private static partnerDetails(id: string): PartnerDetailApiDto | null {
    const allOrganizations = getOrganizations();
    const org = allOrganizations.find(
      (p) => p.id === id && p.admissionState === 'APPROVED' && p.activityState === 'ACTIVE'
    );
    if (!org) return null;

    return {
      id: org.id,
      bpnl: org.bpnl ?? '',
      did: org.did ?? '',
      legalName: org.legalName,
      shortName: org.shortName,
      sector: org.sector,
      legalForm: org.legalForm,
      website: org.website,
      description: org.description,
      activityState: org.activityState as ActivityState,
      taxIdNumber: org.taxIdNumber ?? null,
      commercialRegisterNumber: org.commercialRegisterNumber ?? null,
      commercialRegisterCourt: org.commercialRegisterCourt ?? null,
      street: org.street,
      houseNumber: org.houseNumber,
      postalCode: org.postalCode,
      city: org.city,
      country: org.country,
      representativeName: org.representative
        ? `${org.representative.firstName} ${org.representative.lastName}`
        : null,
      representativeEmail: org.representative?.email ?? null
    };
  }

  private static membersByOrgIdPaginated(orgId: string, request: any) {
    const members = getMembers(orgId);
    const page = parseInt(request.queryParams?.page || '0', 10);
    const size = parseInt(request.queryParams?.size || '10', 10);

    const totalElements = members.length;
    const totalPages = Math.ceil(totalElements / size) || 1;
    const startIdx = page * size;
    const endIdx = Math.min(startIdx + size, totalElements);
    const paginatedContent = members.slice(startIdx, endIdx);

    return {
      content: paginatedContent,
      pageable: {
        pageNumber: page,
        pageSize: size,
        sort: { unsorted: true, sorted: false, empty: true },
        offset: startIdx,
        unpaged: false,
        paged: true
      },
      totalElements,
      totalPages,
      last: page >= totalPages - 1,
      first: page === 0,
      numberOfElements: paginatedContent.length,
      size,
      number: page,
      sort: { unsorted: true, sorted: false, empty: true },
      empty: totalElements === 0
    };
  }

  private static memberDetailsById(memberId: string) {
    return getMemberDetails(memberId);
  }

  private static currentUserMember() {
    const iamId = getMockedUserId();
    return getMemberByIamId(iamId);
  }

  private static organizationDetails(id: string) {
    const allOrganizations = getOrganizations();
    const org = allOrganizations.find((p) => p.id === id);
    if (!org) return null;

    return {
      organization: {
        id: org.id,
        bpnl: org.bpnl ?? '',
        did: org.did ?? '',
        legalName: org.legalName,
        shortName: org.shortName,
        sector: org.sector,
        legalForm: org.legalForm,
        website: org.website,
        description: org.description,
        foundingDate: org.foundingDate,
        street: org.street,
        houseNumber: org.houseNumber,
        postalCode: org.postalCode,
        city: org.city,
        country: org.country,
        commercialRegisterNumber: org.commercialRegisterNumber,
        commercialRegisterCourt: org.commercialRegisterCourt,
        taxIdNumber: org.taxIdNumber,
        admissionState: org.admissionState,
        activityState: org.activityState,
        createdDate: org.createdDate ?? ''
      },
      representative: {
        id: 'rep-' + org.id,
        organizationId: org.id,
        title: org.representative.title,
        firstName: org.representative.firstName,
        lastName: org.representative.lastName,
        position: org.representative.position,
        email: org.representative.email,
        phoneNumber: org.representative.phoneNumber,
        roles: org.representative.roles || ['ORGANIZATION_REPRESENTATIVE'],
        activityState: ActivityState.ACTIVE
      }
    };
  }

  /**
   * Mock for GET /organizations/me — returns the first organization as the current user's org
   */
  private static myOrganizationDetails() {
    const allOrganizations = getOrganizations();
    if (allOrganizations.length === 0)
      return new Response(
        404,
        {},
        JSON.stringify({ error: 'No organization found for current user' })
      );

    const org = allOrganizations[0];
    return {
      organization: {
        id: org.id,
        legalName: org.legalName,
        shortName: org.shortName,
        sector: org.sector,
        legalForm: org.legalForm,
        website: org.website,
        description: org.description,
        foundingDate: org.foundingDate,
        street: org.street,
        houseNumber: org.houseNumber,
        postalCode: org.postalCode,
        city: org.city,
        country: org.country,
        commercialRegisterNumber: org.commercialRegisterNumber,
        commercialRegisterCourt: org.commercialRegisterCourt,
        taxIdNumber: org.taxIdNumber,
        admissionState: org.admissionState,
        activityState: org.activityState,
        createdDate: org.createdDate ?? ''
      },
      representative: {
        id: 'rep-' + org.id,
        organizationId: org.id,
        title: org.representative.title,
        firstName: org.representative.firstName,
        lastName: org.representative.lastName,
        position: org.representative.position,
        email: org.representative.email,
        phoneNumber: org.representative.phoneNumber,
        roles: org.representative.roles || ['ORGANIZATION_REPRESENTATIVE'],
        activityState: ActivityState.ACTIVE
      }
    };
  }

  private static myOrganizationRaw(): {
    id: string;
    legalName: string;
    representativeName: string;
  } {
    const allOrganizations = getOrganizations();
    const org = allOrganizations[0];
    return {
      id: org.id,
      legalName: org.legalName,
      representativeName: `${org.representative.firstName} ${org.representative.lastName}`
    };
  }

  private static updateOrganizationState(
    id: string,
    newState: AdmissionState,
    reasoning: string = ''
  ) {
    const allOrganizations = getOrganizations();
    const organization = allOrganizations.find((p) => p.id === id);
    if (organization) {
      // Determine activityState based on admission state
      const activityState =
        newState === AdmissionState.APPROVED ? ActivityState.ACTIVE : ActivityState.INACTIVE;

      // Persist the state change
      updateOrganizationStateChange(id, newState, activityState);

      // Save reasoning as a comment
      if (reasoning) {
        const commentType =
          newState === AdmissionState.APPROVED ? CommentType.APPROVAL : CommentType.REJECTION;
        const comment = {
          id: `c${Date.now()}`,
          organizationId: id,
          text: reasoning,
          date: new Date().toISOString(),
          type: commentType,
          author: 'Admin'
        };
        addComment(comment);
      }

      return true;
    }
    return null;
  }

  private static comments() {
    return getComments();
  }

  private static commentsByOrganization(organizationId: string) {
    return getComments(organizationId);
  }

  // Auth mock methods
  public getMockedAuthToken(): string | null {
    return getMockedAuthToken();
  }

  public getMockedUserRoles(): string[] {
    return getMockedUserRoles();
  }

  public isLoggedIn(): boolean {
    return isLoggedIn();
  }

  public getAuthMockMode$() {
    return getAuthMockMode$();
  }
}
